import {useCallback, useState} from 'react'
import {Edit, KeyRound, ListChecks, Power, PowerOff, Plus, Trash2} from 'lucide-react'
import {useParams} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useAuth} from '@/contexts/auth-context'
import {Button} from '@/components/ui/button'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Input} from '@/components/ui/input'
import {Textarea} from '@/components/ui/textarea'
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {Icon} from '@/components/shared/Icon'
import {InlineRow} from '@/components/shared/InlineRow'
import {MutedText} from '@/components/shared/MutedText'
import {DetailField} from '@/components/shared/DetailField'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {EmptyState} from '@/components/shared/EmptyState'
import {CodeText} from '@/components/shared/CodeText'
import {PageLayout} from '@/components/shared/PageLayout'
import {DialogForm} from '@/components/shared/DialogForm'
import {FormField} from '@/components/shared/FormField'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {CONCEPT_ICON} from '@/lib/concepts'
import {permissions} from '@/lib/permissions'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {formatTimestamp} from '@/lib/date-utils'
import {ApiError} from '@/api/client'
import {useDialog} from '@/hooks/useDialog'
import {ApiKeyBadge} from '@/types/service-account'
import type {ApiKey} from '@/types/service-account'
import {
    useServiceAccount,
    useApiKeys,
    useUpdateServiceAccount,
    useDeactivateServiceAccount,
} from '@/features/service-accounts/hooks/useServiceAccounts'
import {MintApiKeyDialog} from '@/features/service-accounts/components/MintApiKeyDialog'
import {RevokeApiKeyDialog} from '@/features/service-accounts/components/RevokeApiKeyDialog'
import {AssignRoleDialog} from '@/features/service-accounts/components/AssignRoleDialog'
import {RemoveRoleDialog} from '@/features/service-accounts/components/RemoveRoleDialog'
import {useServiceAccountRoles} from '@/features/service-accounts/hooks/useServiceAccountRoles'
import {TextLink} from '@/components/shared/TextLink'

const DEFAULT_RATE_LIMIT_LABEL = 600

function formatRelativeTimestamp(value: string | null, neverLabel: string): string {
    if (!value) return neverLabel
    return formatTimestamp(value)
}

function ApiKeyStatusBadge({apiKey}: {apiKey: ApiKey}) {
    const i18n = useI18n()
    const badge = ApiKeyBadge.fromKey(apiKey)
    return (
        <Badge variant={badge.variant} data-testid={`badge-key-status-${apiKey.id}`}>
            {badge.label(i18n)}
        </Badge>
    )
}

export function ServiceAccountDetail() {
    const i18n = useI18n()
    const {hasPermission} = useAuth()
    const {id} = useParams<{id: string}>()
    usePageTitle(i18n.serviceAccounts.pageTitle as string)

    const canUpdate = hasPermission(permissions.serviceAccounts.update)
    const canDelete = hasPermission(permissions.serviceAccounts.delete)
    const canMint = hasPermission(permissions.apiKeys.create)
    const canRevoke = hasPermission(permissions.apiKeys.revoke)
    const canViewKeys = hasPermission(permissions.apiKeys.view)
    const canViewRoles = hasPermission(permissions.roleAssignments.view)
    const canManageRoles = hasPermission(permissions.roleAssignments.manage)

    const {data: sa, isLoading: saLoading, error: saError} = useServiceAccount(id)
    const apiKeysQuery = useApiKeys(canViewKeys ? id : undefined)
    const rolesQuery = useServiceAccountRoles(canViewRoles ? id : undefined)
    const updateMutation = useUpdateServiceAccount(id ?? '')
    const deactivateMutation = useDeactivateServiceAccount(id ?? '')

    const editDialog = useDialog()
    const deactivateDialog = useDialog()
    const mintDialog = useDialog()
    const assignRoleDialog = useDialog()
    const [removeRoleTarget, setRemoveRoleTarget] = useState<{roleId: string; roleName: string} | null>(null)
    const [revokeTarget, setRevokeTarget] = useState<{id: string; name: string} | null>(null)
    const [editName, setEditName] = useState('')
    const [editDescription, setEditDescription] = useState('')
    const [editNameError, setEditNameError] = useState<string | null>(null)

    const openEdit = useCallback(() => {
        if (!sa) return
        setEditName(sa.name)
        setEditDescription(sa.description ?? '')
        setEditNameError(null)
        editDialog.open()
    }, [sa, editDialog])

    const onEditOpenChange = useCallback((open: boolean) => {
        if (!open) setEditNameError(null)
        editDialog.bind.onOpenChange(open)
    }, [editDialog.bind])

    const submitEdit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        const trimmed = editName.trim()
        if (!trimmed) {
            setEditNameError(i18n.serviceAccounts.nameRequired as string)
            return
        }
        if (trimmed.length > 100) {
            setEditNameError(i18n.serviceAccounts.nameTooLong as string)
            return
        }
        if (editDescription.length > 500) {
            setEditNameError(i18n.serviceAccounts.descriptionTooLong as string)
            return
        }
        setEditNameError(null)
        updateMutation.mutate(
            {name: trimmed, description: editDescription.trim() || null},
            {
                onSuccess: () => editDialog.close(),
                onError: (err) => {
                    if (err instanceof ApiError && err.status === 409) {
                        setEditNameError(i18n.serviceAccounts.duplicateNameError as string)
                    }
                },
            },
        )
    }, [editName, editDescription, updateMutation, editDialog, i18n.serviceAccounts])

    const submitDeactivate = useCallback(() => {
        deactivateMutation.mutate(undefined, {
            onSuccess: () => deactivateDialog.close(),
        })
    }, [deactivateMutation, deactivateDialog])

    const submitReactivate = useCallback(() => {
        if (!sa) return
        updateMutation.mutate({name: sa.name, description: sa.description, active: true})
    }, [sa, updateMutation])

    const apiKeys = apiKeysQuery.data ?? []
    const isInactive = sa ? !sa.active : false

    return (
        <PageLayout
            variant="detail"
            icon={CONCEPT_ICON.serviceAccount}
            title={sa?.name ?? ''}
            data-testid="service-account-detail-page"
            backTo={routes.admin.serviceAccounts.list()}
            loading={saLoading}
            error={saError ?? (!saLoading ? notFoundIfMissing(sa, 'SERVICE_ACCOUNT') : undefined)}
            description={sa ? (
                <InlineRow>
                    <Badge
                        variant={sa.active ? BadgeVariant.SUCCESS : BadgeVariant.SECONDARY}
                        data-testid="badge-detail-status"
                    >
                        {sa.active
                            ? i18n.serviceAccounts.statusActive
                            : i18n.serviceAccounts.statusInactive}
                    </Badge>
                </InlineRow>
            ) : undefined}
        >
            {!sa ? null : (
                    <>
                        <Card data-testid="card-overview" data-help-prefix="overview">
                            <CardHeader>
                                <InlineRow justify="between" align="start">
                                    <div>
                                        <CardTitle data-testid="card-overview-title" data-help-key="_section">
                                            <InlineRow gap="sm">
                                                <Icon icon={KeyRound}/>
                                                {i18n.serviceAccounts.overview}
                                            </InlineRow>
                                        </CardTitle>
                                        <CardDescription>
                                            {i18n.serviceAccounts.pageDescription}
                                        </CardDescription>
                                    </div>
                                    {canUpdate && (
                                        <InlineRow gap="sm">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={openEdit}
                                                data-testid="btn-edit-service-account"
                                            >
                                                <Icon icon={Edit} className="mr-2"/>
                                                {i18n.serviceAccounts.editButton}
                                            </Button>
                                            {sa.active && canDelete && (
                                                <Button
                                                    size="sm"
                                                    variant="destructive"
                                                    onClick={deactivateDialog.open}
                                                    data-testid="btn-deactivate-service-account"
                                                >
                                                    <Icon icon={PowerOff} className="mr-2"/>
                                                    {i18n.serviceAccounts.deactivateButton}
                                                </Button>
                                            )}
                                            {!sa.active && canDelete && (
                                                <Button
                                                    size="sm"
                                                    variant="default"
                                                    onClick={submitReactivate}
                                                    data-testid="btn-reactivate-service-account"
                                                    disabled={updateMutation.isPending}
                                                >
                                                    <Icon icon={Power} className="mr-2"/>
                                                    {i18n.serviceAccounts.reactivateButton}
                                                </Button>
                                            )}
                                        </InlineRow>
                                    )}
                                </InlineRow>
                            </CardHeader>
                            <CardContent>
                                <DetailGrid columns={2}>
                                    <DetailField
                                        label={i18n.serviceAccounts.columns.name as string}
                                        value={sa.name}
                                    />
                                    <DetailField
                                        label={i18n.serviceAccounts.columns.status as string}
                                        value={
                                            <Badge variant={sa.active ? BadgeVariant.SUCCESS : BadgeVariant.SECONDARY}>
                                                {sa.active
                                                    ? i18n.serviceAccounts.statusActive
                                                    : i18n.serviceAccounts.statusInactive}
                                            </Badge>
                                        }
                                    />
                                    <DetailField
                                        label={i18n.serviceAccounts.columns.description as string}
                                        value={sa.description ?? '—'}
                                        fullWidth
                                    />
                                    <DetailField
                                        label={i18n.serviceAccounts.columns.created as string}
                                        value={formatTimestamp(sa.createdAt)}
                                    />
                                    <DetailField
                                        label={i18n.serviceAccounts.columns.activeKeys as string}
                                        value={
                                            <Badge variant="outline" data-testid="badge-detail-active-keys">
                                                {sa.activeKeyCount}
                                            </Badge>
                                        }
                                    />
                                </DetailGrid>
                            </CardContent>
                        </Card>

                        <Card data-testid="card-api-keys" data-help-prefix="apiKeys">
                            <CardHeader>
                                <InlineRow justify="between" align="start">
                                    <div>
                                        <CardTitle data-testid="card-api-keys-title" data-help-key="_section">
                                            <InlineRow gap="sm">
                                                <Icon icon={KeyRound}/>
                                                {i18n.apiKeys.cardTitle}
                                            </InlineRow>
                                        </CardTitle>
                                        <CardDescription>
                                            {i18n.apiKeys.cardCount({count: apiKeys.length})}
                                        </CardDescription>
                                    </div>
                                    {canMint && !isInactive && (
                                        <Button
                                            size="sm"
                                            onClick={mintDialog.open}
                                            data-testid="btn-mint-api-key"
                                        >
                                            <Icon icon={Plus} className="mr-2"/>
                                            {i18n.apiKeys.mintButton}
                                        </Button>
                                    )}
                                </InlineRow>
                            </CardHeader>
                            <CardContent>
                                {apiKeysQuery.isLoading && (
                                    <MutedText block centered className="py-8">
                                        {i18n.common.loading}
                                    </MutedText>
                                )}
                                {apiKeysQuery.error && (
                                    <MutedText block centered className="py-8" data-testid="api-keys-error">
                                        {String((apiKeysQuery.error as Error).message)}
                                    </MutedText>
                                )}
                                {!apiKeysQuery.isLoading && !apiKeysQuery.error && apiKeys.length === 0 && (
                                    <EmptyState
                                        data-testid="api-keys-empty"
                                        title={i18n.apiKeys.emptyTitle as string}
                                        description={i18n.apiKeys.emptyDescription as string}
                                        action={canMint && !isInactive ? (
                                            <Button
                                                onClick={mintDialog.open}
                                                data-testid="btn-mint-empty"
                                            >
                                                <Icon icon={Plus} className="mr-2"/>
                                                {i18n.apiKeys.mintButton}
                                            </Button>
                                        ) : undefined}
                                    />
                                )}
                                {!apiKeysQuery.isLoading && !apiKeysQuery.error && apiKeys.length > 0 && (
                                    <Table>
                                        <TableHeader>
                                            <TableRow data-readonly>
                                                <TableHead data-testid="col-key-name" data-help-key="name">
                                                    {i18n.apiKeys.columns.name}
                                                </TableHead>
                                                <TableHead data-testid="col-key-prefix" data-help-key="prefix">
                                                    {i18n.apiKeys.columns.prefix}
                                                </TableHead>
                                                <TableHead data-testid="col-key-last-used" data-help-key="lastUsed">
                                                    {i18n.apiKeys.columns.lastUsed}
                                                </TableHead>
                                                <TableHead data-testid="col-key-rate-limit" data-help-key="rateLimit">
                                                    {i18n.apiKeys.columns.rateLimit}
                                                </TableHead>
                                                <TableHead data-testid="col-key-status" data-help-key="status">
                                                    {i18n.apiKeys.columns.status}
                                                </TableHead>
                                                {canRevoke && (
                                                    <TableHead data-testid="col-key-actions">
                                                        {i18n.apiKeys.columns.actions}
                                                    </TableHead>
                                                )}
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {apiKeys.map((key) => {
                                                const showRevoke = canRevoke && key.active && !key.revokedAt
                                                return (
                                                    <TableRow key={key.id} data-testid={`api-key-row-${key.id}`} data-readonly>
                                                        <TableCell data-testid={`api-key-name-${key.id}`}>
                                                            {key.name}
                                                        </TableCell>
                                                        <TableCell>
                                                            <CodeText className="text-xs">
                                                                <span data-testid={`api-key-prefix-${key.id}`}>
                                                                    {key.keyPrefix}…
                                                                </span>
                                                            </CodeText>
                                                        </TableCell>
                                                        <TableCell>
                                                            <MutedText size="sm">
                                                                {formatRelativeTimestamp(
                                                                    key.lastUsedAt,
                                                                    i18n.apiKeys.never as string,
                                                                )}
                                                            </MutedText>
                                                        </TableCell>
                                                        <TableCell data-testid={`api-key-rate-limit-${key.id}`}>
                                                            {key.rateLimitPerMinute === null
                                                                ? i18n.apiKeys.defaultRateLimit({value: DEFAULT_RATE_LIMIT_LABEL})
                                                                : String(key.rateLimitPerMinute)}
                                                        </TableCell>
                                                        <TableCell>
                                                            <ApiKeyStatusBadge apiKey={key}/>
                                                        </TableCell>
                                                        {canRevoke && (
                                                            <TableCell>
                                                                {showRevoke && (
                                                                    <Button
                                                                        size="sm"
                                                                        variant="ghost"
                                                                        onClick={() => setRevokeTarget({id: key.id, name: key.name})}
                                                                        data-testid={`btn-revoke-${key.id}`}
                                                                    >
                                                                        <Icon icon={Trash2} className="mr-1"/>
                                                                        {i18n.apiKeys.revokeButton}
                                                                    </Button>
                                                                )}
                                                            </TableCell>
                                                        )}
                                                    </TableRow>
                                                )
                                            })}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>

                        <Card data-testid="card-roles" data-help-prefix="roles">
                            <CardHeader>
                                <InlineRow justify="between" align="start">
                                    <div>
                                        <CardTitle data-testid="card-roles-title" data-help-key="_section">
                                            <InlineRow gap="sm">
                                                <Icon icon={ListChecks}/>
                                                {i18n.serviceAccounts.roles}
                                            </InlineRow>
                                        </CardTitle>
                                        <CardDescription>{i18n.serviceAccounts.rolesDescription}</CardDescription>
                                    </div>
                                    {canManageRoles && (
                                        <Button
                                            size="sm"
                                            onClick={assignRoleDialog.open}
                                            data-testid="btn-assign-role"
                                        >
                                            <Icon icon={Plus} className="mr-2"/>
                                            {i18n.serviceAccounts.assignRoleButton}
                                        </Button>
                                    )}
                                </InlineRow>
                            </CardHeader>
                            <CardContent>
                                {rolesQuery.isLoading && (
                                    <MutedText block centered className="py-8" data-testid="roles-loading">
                                        {i18n.common.loading}
                                    </MutedText>
                                )}
                                {rolesQuery.error && (
                                    <MutedText block centered className="py-8" data-testid="roles-error">
                                        {String((rolesQuery.error as Error).message)}
                                    </MutedText>
                                )}
                                {!rolesQuery.isLoading && !rolesQuery.error && (rolesQuery.data ?? []).length === 0 && (
                                    <EmptyState
                                        data-testid="roles-empty"
                                        title={i18n.serviceAccounts.rolesEmpty as string}
                                        action={canManageRoles && !isInactive ? (
                                            <Button
                                                onClick={assignRoleDialog.open}
                                                data-testid="btn-assign-role-empty"
                                            >
                                                <Icon icon={Plus} className="mr-2"/>
                                                {i18n.serviceAccounts.assignRoleButton}
                                            </Button>
                                        ) : undefined}
                                    />
                                )}
                                {!rolesQuery.isLoading && !rolesQuery.error && (rolesQuery.data ?? []).length > 0 && (
                                    <Table>
                                        <TableHeader>
                                            <TableRow data-readonly>
                                                <TableHead data-testid="col-role-name" data-help-key="name">
                                                    {i18n.serviceAccounts.roleColumns.name}
                                                </TableHead>
                                                <TableHead data-testid="col-role-description" data-help-key="description">
                                                    {i18n.serviceAccounts.roleColumns.description}
                                                </TableHead>
                                                <TableHead data-testid="col-role-assigned-by" data-help-key="assignedBy">
                                                    {i18n.serviceAccounts.roleColumns.assignedBy}
                                                </TableHead>
                                                <TableHead data-testid="col-role-assigned-at" data-help-key="assignedAt">
                                                    {i18n.serviceAccounts.roleColumns.assignedAt}
                                                </TableHead>
                                                {canManageRoles && (
                                                    <TableHead data-testid="col-role-actions">
                                                        {i18n.serviceAccounts.roleColumns.actions}
                                                    </TableHead>
                                                )}
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {(rolesQuery.data ?? []).map((assignment) => (
                                                <TableRow
                                                    key={assignment.roleId}
                                                    data-testid={`role-row-${assignment.roleId}`}
                                                    data-readonly
                                                >
                                                    <TableCell data-testid={`role-name-${assignment.roleId}`}>
                                                        <TextLink
                                                            to={routes.admin.roles.detail(assignment.roleId)}
                                                            testId={`role-link-${assignment.roleId}`}
                                                        >
                                                            {assignment.roleName}
                                                        </TextLink>
                                                    </TableCell>
                                                    <TableCell data-testid={`role-description-${assignment.roleId}`}>
                                                        <MutedText size="sm">
                                                            {assignment.roleDescription ?? '—'}
                                                        </MutedText>
                                                    </TableCell>
                                                    <TableCell data-testid={`role-assigned-by-${assignment.roleId}`}>
                                                        <MutedText size="sm">
                                                            {assignment.assignedByDisplayName ?? '—'}
                                                        </MutedText>
                                                    </TableCell>
                                                    <TableCell data-testid={`role-assigned-at-${assignment.roleId}`}>
                                                        <MutedText size="sm">
                                                            {formatTimestamp(assignment.assignedAt)}
                                                        </MutedText>
                                                    </TableCell>
                                                    {canManageRoles && (
                                                        <TableCell>
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => setRemoveRoleTarget({
                                                                    roleId: assignment.roleId,
                                                                    roleName: assignment.roleName,
                                                                })}
                                                                data-testid={`btn-remove-role-${assignment.roleId}`}
                                                            >
                                                                <Icon icon={Trash2} className="mr-1"/>
                                                                {i18n.serviceAccounts.removeRoleAction}
                                                            </Button>
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>

                        <Dialog open={editDialog.isOpen} onOpenChange={onEditOpenChange}>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle data-testid="edit-dialog-title">
                                        {i18n.serviceAccounts.editTitle}
                                    </DialogTitle>
                                    <DialogDescription>
                                        {i18n.serviceAccounts.pageDescription}
                                    </DialogDescription>
                                </DialogHeader>
                                <DialogForm onSubmit={submitEdit} data-testid="edit-form">
                                    <FormField
                                        label={i18n.serviceAccounts.nameLabel as string}
                                        htmlFor="sa-edit-name"
                                        required
                                        error={editNameError ?? undefined}
                                    >
                                        <Input
                                            id="sa-edit-name"
                                            data-testid="edit-input-name"
                                            value={editName}
                                            onChange={(e) => setEditName(e.target.value)}
                                            maxLength={100}
                                            autoFocus
                                        />
                                    </FormField>
                                    <FormField
                                        label={i18n.serviceAccounts.descriptionLabel as string}
                                        htmlFor="sa-edit-description"
                                    >
                                        <Textarea
                                            id="sa-edit-description"
                                            data-testid="edit-input-description"
                                            value={editDescription}
                                            onChange={(e) => setEditDescription(e.target.value)}
                                            placeholder={i18n.serviceAccounts.descriptionPlaceholder as string}
                                            maxLength={500}
                                            rows={3}
                                        />
                                    </FormField>
                                    <SaveCancelFooter
                                        testIdPrefix="edit"
                                        onCancel={editDialog.close}
                                        saving={updateMutation.isPending}
                                        saveLabel={i18n.common.save as string}
                                    />
                                </DialogForm>
                            </DialogContent>
                        </Dialog>

                        <Dialog open={deactivateDialog.isOpen} onOpenChange={deactivateDialog.bind.onOpenChange}>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle data-testid="deactivate-dialog-title">
                                        {i18n.serviceAccounts.deactivateTitle}
                                    </DialogTitle>
                                    <DialogDescription data-testid="deactivate-dialog-body">
                                        {i18n.serviceAccounts.deactivateBody}
                                    </DialogDescription>
                                </DialogHeader>
                                <SaveCancelFooter
                                    testIdPrefix="deactivate"
                                    onCancel={deactivateDialog.close}
                                    saving={deactivateMutation.isPending}
                                    saveLabel={i18n.serviceAccounts.deactivateConfirm as string}
                                    savingLabel={i18n.serviceAccounts.deactivateConfirm as string}
                                    destructive
                                    saveType="button"
                                    onSave={submitDeactivate}
                                />
                            </DialogContent>
                        </Dialog>

                        {sa && (
                            <MintApiKeyDialog
                                serviceAccountId={sa.id}
                                open={mintDialog.isOpen}
                                onOpenChange={(next) => (next ? mintDialog.open() : mintDialog.close())}
                            />
                        )}

                        {sa && (
                            <RevokeApiKeyDialog
                                serviceAccountId={sa.id}
                                keyId={revokeTarget?.id ?? null}
                                keyName={revokeTarget?.name ?? ''}
                                open={revokeTarget !== null}
                                onOpenChange={(next) => {
                                    if (!next) setRevokeTarget(null)
                                }}
                            />
                        )}

                        {sa && (
                            <AssignRoleDialog
                                serviceAccountId={sa.id}
                                excludeRoleIds={(rolesQuery.data ?? []).map((a) => a.roleId)}
                                open={assignRoleDialog.isOpen}
                                onOpenChange={(next) => (next ? assignRoleDialog.open() : assignRoleDialog.close())}
                            />
                        )}

                        {sa && (
                            <RemoveRoleDialog
                                serviceAccountId={sa.id}
                                serviceAccountName={sa.name}
                                roleId={removeRoleTarget?.roleId ?? null}
                                roleName={removeRoleTarget?.roleName ?? ''}
                                open={removeRoleTarget !== null}
                                onOpenChange={(next) => {
                                    if (!next) setRemoveRoleTarget(null)
                                }}
                            />
                        )}
                    </>
                )}
        </PageLayout>
    )
}

